// src/services/giftCodeService.ts
import { db, appId } from '../firebase';
import { collection, doc, getDoc, getDocs, query, where, updateDoc, setDoc, serverTimestamp, runTransaction, Timestamp } from 'firebase/firestore'; // setDoc をインポート
import { GiftCode } from '../types'; // GiftCodeの型定義をインポート
import { logger } from '../utils/logger';

const GIFT_CODE_COLLECTION_PATH = `artifacts/${appId}/public/data/giftcode`;

/**
 * Firestoreから特定のギフトコードを取得する関数
 * @param giftCode - 取得するギフトコード
 * @returns ギフトコードデータ、またはnull
 */
export const fetchGiftCode = async (giftCode: string): Promise<GiftCode | null> => {
    if (!giftCode) return null;
    const giftCodeRef = doc(db, GIFT_CODE_COLLECTION_PATH, giftCode);
    const snapshot = await getDoc(giftCodeRef);
    return snapshot.exists() ? ({ giftCode: snapshot.id, ...snapshot.data() } as GiftCode) : null;
};

/**
 * ギフトコードをFirestoreに追加する関数
 * @param giftCodeData - 追加するギフトコードのデータ (giftCode, QuizId, max_dl_count, allowed_users を含む)
 * @returns 追加されたギフトコードの完全なデータを持つGiftCodeオブジェクト
 */
export const addGiftCode = async (giftCodeData: Omit<GiftCode, 'dl_count' | 'active' | 'used_by_user_id'>): Promise<GiftCode> => {
    logger.info("add GiftCodeData:", giftCodeData);

    // 生成されたgiftCodeをドキュメントIDとして使用
    const giftCodeRef = doc(db, GIFT_CODE_COLLECTION_PATH, giftCodeData.giftCode);

    // Firestoreに設定するデータオブジェクトを構築
    const dataToSet: any = {
        QuizId: giftCodeData.QuizId, // QuizIdは必須
        dl_count: 0,
        active: true,
        used_by_user_id: [], // 初期値を空の配列に設定
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
    };

    // max_dl_count が undefined でない場合のみ含める
    if (giftCodeData.max_dl_count !== undefined) {
        dataToSet.max_dl_count = giftCodeData.max_dl_count;
    }

    // allowed_users が undefined でない場合のみ含める。
    // もし undefined の場合は、Firestoreに空の配列として保存する（制限なしを意味するため）
    if (giftCodeData.allowed_users !== undefined) {
        dataToSet.allowed_users = giftCodeData.allowed_users;
    } else {
        dataToSet.allowed_users = []; // 制限なしの場合、空の配列として保存
    }

    await setDoc(giftCodeRef, dataToSet);

    const newGiftCodeSnap = await getDoc(giftCodeRef); // 同じ参照でドキュメントを取得
    if (newGiftCodeSnap.exists()) {
        const fullGiftCodeData = newGiftCodeSnap.data() as Omit<GiftCode, 'giftCode'>;
        logger.log(`GiftCode added with ID: ${newGiftCodeSnap.id}`);
        return { giftCode: newGiftCodeSnap.id, ...fullGiftCodeData } as GiftCode;
    } else {
        logger.error(`Failed to retrieve newly added gift code with ID: ${giftCodeData.giftCode}`);
        throw new Error("新しいギフトコードデータの取得に失敗しました。");
    }
};

/**
 * ギフトコードをFirestoreで更新する関数
 * @param giftCodeId - 更新するギフトコード
 * @param giftCodeData - 更新するギフトコードのデータ（部分的な更新も可）
 */
export const updateGiftCode = async (giftCodeId: string, giftCodeData: Partial<GiftCode>): Promise<void> => {
    logger.info("update GiftCodeData:", giftCodeData);
    const giftCodeRef = doc(db, GIFT_CODE_COLLECTION_PATH, giftCodeId);
    await updateDoc(giftCodeRef, {
        ...giftCodeData,
        updatedAt: serverTimestamp(),
    });
};

/**
 * ギフトコードをFirestoreから削除する関数
 * @param giftCodeId - 削除するギフトコード
 */
export const deleteGiftCode = async (giftCode: string): Promise<void> => {
    const giftCodeRef = doc(db, GIFT_CODE_COLLECTION_PATH, giftCode);
    await updateDoc(giftCodeRef, {
        active: false,
        updatedAt: serverTimestamp(),
    });
};

/**
 * ギフトコードが使用可能かどうかの条件をチェックする関数。
 * データは更新せず、チェックのみを行います。
 * @param giftCode - チェックするギフトコード
 * @param userId - チェックするユーザーのID
 * @returns チェックに成功した場合、GiftCodeデータ
 * @throws エラーが発生した場合
 */
export const checkGiftCodeEligibility = async (giftCode: string, userId: string): Promise<GiftCode> => {

    logger.log("giftCode:",giftCode);
    logger.log("userId:",userId);
    logger.log("GIFT_CODE_COLLECTION_PATH:",GIFT_CODE_COLLECTION_PATH);

    const giftCodeRef = doc(db, GIFT_CODE_COLLECTION_PATH, giftCode);
    const giftCodeDoc = await getDoc(giftCodeRef);

    logger.log("giftCodeDoc:",giftCodeDoc.data);


    if (!giftCodeDoc.exists()) {
        throw new Error("ギフトコードが見つかりません。");
    }

    const currentGiftCode = giftCodeDoc.data() as GiftCode;

    if (!currentGiftCode.active) {
        throw new Error("このギフトコードは無効です。");
    }

    // 最大ダウンロード数チェック
    if (currentGiftCode.max_dl_count !== undefined && currentGiftCode.dl_count >= currentGiftCode.max_dl_count) {
        throw new Error("このギフトコードはダウンロード上限に達しています。");
    }

    if (currentGiftCode.allowed_users && currentGiftCode.allowed_users.length > 0) {
        // 許可ユーザーリストチェック
        if (!currentGiftCode.allowed_users.includes(userId)) {
            throw new Error("このギフトコードはあなたには許可されていません。");
        }
        // 既にこのユーザーが使用済みかチェック(許可ユーザリストも)
        //修正：クイズを消して、再度追加を行う場合を考慮してこのチェックは行わない
        /*if (currentGiftCode.used_by_user_id && currentGiftCode.used_by_user_id.length > 0 && currentGiftCode.used_by_user_id.includes(userId)) {
        //    throw new Error("このギフトコードは既にあなたによって使用されています。");
        //}*/
    }

    return currentGiftCode; // チェックに成功したら現在のギフトコードデータを返す
};

/**
 * ギフトコードの使用状況を更新する関数
 * @param giftCode - 使用するギフトコードの文字列
 * @param userId - ギフトコードを使用するユーザーID
 * @returns 更新後のギフトコードデータ
 */
export const applyGiftCodeUsage = async (giftCode: string, userId: string): Promise<GiftCode> => {
    // giftCode または userId が null の場合はエラーをスロー
    if (!giftCode || !userId) {
        throw new Error("Gift code or user ID is missing.");
    }

    const giftCodeRef = doc(db, GIFT_CODE_COLLECTION_PATH, giftCode);

    try {
        const updatedGiftCode = await runTransaction(db, async (transaction) => {
            const giftCodeDoc = await transaction.get(giftCodeRef);

            if (!giftCodeDoc.exists()) {
                throw new Error("Invalid gift code.");
            }

            const currentGiftCode = giftCodeDoc.data() as GiftCode;

            // allowed_userの存在を確認
            const isAllowedUserListNotEmpty = currentGiftCode.allowed_users && currentGiftCode.allowed_users.length > 0;
            const updates: Partial<GiftCode> = {};

            // dl_countを更新
            updates.dl_count = (currentGiftCode.dl_count || 0) + 1;

            // allowed_usersが存在し、かつ used_by_user_id にuserIdが含まれていない場合のみ追加
            if (isAllowedUserListNotEmpty) {
                const existingUsers = currentGiftCode.used_by_user_id || [];
                if (!existingUsers.includes(userId)) {
                    updates.used_by_user_id = [...existingUsers, userId];
                }
            }

            updates.updatedAt = serverTimestamp();

            transaction.update(giftCodeRef, updates);

            // トランザクションで返すオブジェクトは、型定義に合わせてローカルで値を設定する
            const now = new Date();
            return {
                ...currentGiftCode,
                ...updates,
                updatedAt: Timestamp.fromDate(now) // DateからTimestampに変換
            } as GiftCode;
        });

        logger.info(`Gift code ${giftCode} usage successfully applied for user ${userId}.`);
        return updatedGiftCode;

    } catch (e) {
        logger.error(`Transaction failed for gift code ${giftCode}: `, e);
        throw e;
    }
};

// --- ギフトコード生成関連のユーティリティ関数 ---

/**
 * ギフトコードの各セグメントで使用できる文字セット。
 * 数字 '1', '0' と文字 'I', 'O' は紛らわしいため除外。
 */
const ALLOWED_CHARS_FOR_SEGMENT = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/**
 * モジュラス11チェックデジット計算のための文字から数値へのマッピング。
 * 各文字に一意の数値を割り当てます。
 */
const CHAR_TO_VALUE_MAP: { [key: string]: number } = (() => {
    const map: { [key: string]: number } = {};
    let value = 0;
    // 数字の割り当て
    for (let i = 0; i <= 9; i++) {
        // '1'と'0'はスキップ
        if (i === 1 || i === 0) continue;
        map[i.toString()] = value++;
    }
    // 大文字アルファベットの割り当て (I, Oを除く)
    for (let charCode = 65; charCode <= 90; charCode++) { // 'A' (65) to 'Z' (90)
        const char = String.fromCharCode(charCode);
        if (char === 'I' || char === 'O') continue;
        map[char] = value++;
    }
    return map;
})();


/**
 * 指定された長さのランダムな英数字セグメントを生成します。
 * 紛らわしい文字 (1, I, 0, O) は使用しません。
 * @param length - 生成するセグメントの長さ
 * @returns 生成されたランダムなセグメント文字列
 */
function generateRandomSegment(length: number): string {
    let result = '';
    const charsLength = ALLOWED_CHARS_FOR_SEGMENT.length;
    for (let i = 0; i < length; i++) {
        result += ALLOWED_CHARS_FOR_SEGMENT.charAt(Math.floor(Math.random() * charsLength));
    }
    return result;
}

/**
 * モジュラス11チェックデジットを計算します。
 * @param baseCode - チェックデジットを計算する基になる文字列（ハイフンなしの12桁）
 * @returns 計算されたチェックデジット (0-9または'X')
 */
function calculateMod11CheckDigit(baseCode: string): string {
    let sum = 0;
    // ウェイトは右から左へ 2, 3, 4, ... と増加
    // 12桁のコードなので、ウェイトは 13, 12, ..., 2 となる
    for (let i = 0; i < baseCode.length; i++) {
        const char = baseCode[i];
        const value = CHAR_TO_VALUE_MAP[char];
        if (value === undefined) {
            // 定義されていない文字が来た場合はエラー
            throw new Error(`Invalid character for Modulo 11 calculation: ${char} `);
        }
        // ウェイトは (baseCode.length - i + 1) で計算
        // 例: 12文字目のウェイトは (12-11+1) = 2
        // 1文字目のウェイトは (12-0+1) = 13
        const weight = baseCode.length - i + 1;
        sum += value * weight;
    }

    const remainder = sum % 11;
    const checkDigitValue = (11 - remainder) % 11; // 11の場合0、10の場合10

    if (checkDigitValue === 10) {
        return 'X';
    } else {
        return checkDigitValue.toString();
    }
}

/**
 * モジュラス11チェックデジット付きのギフトコードを生成します。
 * 形式: 4桁-4桁-4桁-チェックデジット
 * 各4桁のセグメントは英数字（大文字のみ、1, I, 0, Oを除く）で構成されます。
 * @returns 生成されたギフトコード
 */
export function generateGiftCodeWithMod11(): string {
    const segment1 = generateRandomSegment(4);
    const segment2 = generateRandomSegment(4);
    const segment3 = generateRandomSegment(4);

    const baseCode = segment1 + segment2 + segment3;
    const checkDigit = calculateMod11CheckDigit(baseCode);

    return segment1 + "-" + segment2 + "-" + segment3 + "-" + checkDigit;
}
